import { ApolloError, gql } from "@apollo/client";
import { GET_ORGANIZATIONS } from "src/scenes/admin/organizations/graphql/queries";
import { Status } from "src/types/authData";
import { HasuraRole } from "src/types/hasuraRole";
import * as RD from "src/types/remoteData";
import * as GQL from "../types/graphql";
import * as RemoteData from "../types/remoteData";
import { GET_ORGANIZATIONS_BY_USER_ID } from "./graphql/queries";
import { useRemoteDataQuery } from "./useRemoteDataQuery";
import useRequiredHasuraRoles from "./useRequiredHasuraRoles";
import useUser from "./useUser";

export function useOrganizations(): RemoteData.RemoteData<
  ApolloError,
  GQL.GetOrganizations_organization[]
> {
  const { remoteData } =
    useRemoteDataQuery<GQL.GetOrganizations>(GET_ORGANIZATIONS);
  return remoteData.map((data) => data.organization);
}

type Organization = {
  id: uuid;
  name: string;
  path: string;
};
export function useAdminOrganizations(): RemoteData.RemoteData<
  Error,
  Organization[]
> {
  const user = useUser();
  const isAvelaAdmin = useRequiredHasuraRoles([HasuraRole.ADMIN]);
  const isAdmin = useRequiredHasuraRoles([
    HasuraRole.DISTRICT_ADMIN,
    HasuraRole.ORG_ADMIN,
    HasuraRole.SCHOOL_ADMIN,
  ]);
  const { remoteData: avelaAdminOrganizationsRD } =
    useRemoteDataQuery<GQL.GetAllOrganizationsForAvelaAdmin>(
      gql`
        query GetAllOrganizationsForAvelaAdmin {
          organization(order_by: { name: asc }) {
            id
            path
            name
          }
        }
      `,
      { skip: !isAvelaAdmin }
    );

  const { remoteData: adminOrganizationsRD } = useRemoteDataQuery<
    GQL.GetOrganizationsForAdminByUserId,
    GQL.GetOrganizationsForAdminByUserIdVariables
  >(
    gql`
      query GetOrganizationsForAdminByUserId($userId: String!) {
        person(
          where: {
            user_id: { _eq: $userId }
            person_type: { _nin: [admin, guardian, applicant] }
          }
        ) {
          organization {
            id
            path
            name
          }
        }
      }
    `,
    {
      variables: {
        userId: user.status === Status.OK ? user.data.id : "",
      },
      skip: !isAdmin || user.status !== Status.OK,
    }
  );

  if (isAvelaAdmin) {
    return avelaAdminOrganizationsRD.map((orgs) =>
      orgs.organization.map((org) => org)
    );
  } else if (isAdmin) {
    return adminOrganizationsRD.map((orgs) => {
      return orgs.person.flatMap((p) =>
        p.organization ? [p.organization] : []
      );
    });
  }

  return RD.failure(new Error("Require admin role"));
}

export function useParentOrganizations(): RemoteData.RemoteData<
  ApolloError,
  Organization[]
> {
  const user = useUser();
  const userId = user.status === Status.OK ? user.data.id : null;
  const { remoteData } = useRemoteDataQuery<
    GQL.GetOrganizationsByUserId,
    GQL.GetOrganizationsByUserIdVariables
  >(GET_ORGANIZATIONS_BY_USER_ID, {
    variables: { userId: userId ?? "" },
    skip: userId === null,
  });

  return remoteData.map((data) => data.organization);
}
